import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, collection, query, where, onSnapshot,
  updateDoc, addDoc, getDocs, runTransaction, increment,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { getFullMatch, checkWinner, updatePlayerStats } from '../utils/matchHelpers';
import { computeNextServe } from '../utils/serve-logic';
import { computeSinglesNextServe, getSinglesServeSide } from '../utils/singles-serve-logic';
import CourtLayout from '../components/CourtLayout';
import ScoreAnimation from '../components/ScoreAnimation';
import MatchTimer from '../components/MatchTimer';

export default function LiveMatch() {
  const { id } = useParams();
  const { admin } = useAuth();
  const navigate = useNavigate();

  const [match, setMatch] = useState(null);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [animation, setAnimation] = useState(null);
  const [view, setView] = useState('score');
  const [courtPos, setCourtPos] = useState({});
  const [serverTeam, setServerTeam] = useState(1);
  const [courtNumber, setCourtNumber] = useState(1);
  const [saving, setSaving] = useState(false);
  const [connected, setConnected] = useState(false);
  const [confetti, setConfetti] = useState([]);
  const [toastNotif, setToastNotif] = useState(null);
  const [setWinnerBanner, setSetWinnerBanner] = useState(null);
  const [startingServerPlayerId, setStartingServerPlayerId] = useState(null);
  const [dialog, setDialog] = useState(null);

  const namesRef = useRef({});
  const staticRef = useRef(null);

  const tryParseJson = (s) => {
    try { return s ? JSON.parse(s) : {}; } catch { return {}; }
  };

  // Initial load: enriched data with team/player names
  useEffect(() => {
    getFullMatch(id).then(m => {
      if (!m) { setLoading(false); return; }
      namesRef.current = {
        team1_id: m.team1_id, team2_id: m.team2_id,
        team1_name: m.team1_name, team2_name: m.team2_name,
        t1_p1_id: m.t1_p1_id, t1_p1_name: m.t1_p1_name,
        t1_p2_id: m.t1_p2_id, t1_p2_name: m.t1_p2_name,
        t2_p1_id: m.t2_p1_id, t2_p1_name: m.t2_p1_name,
        t2_p2_id: m.t2_p2_id, t2_p2_name: m.t2_p2_name,
        tournament_id: m.tournament_id, tournament_name: m.tournament_name,
        tournament_poster_url: m.tournament_poster_url,
      };
      staticRef.current = {
        team1: { player1_id: m.t1_p1_id, player2_id: m.t1_p2_id },
        team2: { player1_id: m.t2_p1_id, player2_id: m.t2_p2_id },
      };
      setMatch(prev => prev ? { ...namesRef.current, ...prev } : m);
      setCourtPos(tryParseJson(m.court_positions));
      setServerTeam(m.current_server_team === m.team2_id ? 2 : 1);
      setCourtNumber(m.court_number || 1);
      if (m.current_server_player_id) setStartingServerPlayerId(m.current_server_player_id);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  // Live match updates via Firestore
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'matches', id), (snap) => {
      if (!snap.exists()) return;
      const data = { id: snap.id, ...snap.data() };
      setMatch(() => ({ ...namesRef.current, ...data }));
      setCourtPos(tryParseJson(data.court_positions));
      setConnected(true);
    });
    return unsub;
  }, [id]);

  // Point log — sorted client-side to avoid composite index requirement
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'points'), where('match_id', '==', id)),
      (snap) => {
        const pts = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        setPoints(pts);
      }
    );
    return unsub;
  }, [id]);

  const triggerWinAnimation = (m) => {
    setAnimation({ type: 'win', match: m, teamId: m.winner_team_id });
    const pieces = Array.from({ length: 50 }, (_, i) => ({
      id: i, x: Math.random() * 100, delay: Math.random() * 1.5,
      color: ['#27ae60','#2980b9','#f39c12','#e74c3c','#8e44ad','#1abc9c'][Math.floor(Math.random()*6)],
      size: 6 + Math.random() * 8,
    }));
    setConfetti(pieces);
    setTimeout(() => { setAnimation(null); setConfetti([]); }, 4000);
  };

  const showDialog = (title, message, onConfirm) => setDialog({ title, message, onConfirm });

  const handleSetup = async () => {
    const isSinglesMatch = match?.game_type === 'singles';
    if (match?.game_type === 'doubles') {
      const zones = ['TL','BL','TR','BR'];
      if (zones.filter(z => courtPos[z]).length < 4) {
        showDialog('Positions Required', 'Please assign all 4 players to court positions before saving.', null);
        return;
      }
      if (!startingServerPlayerId) {
        showDialog('Server Required', 'Please select who serves first for the starting team.', null);
        return;
      }
    }
    setSaving(true);
    // For singles, auto-assign server player from the selected team's player1.
    const serverPlayerId = isSinglesMatch
      ? (serverTeam === 1 ? match?.t1_p1_id : match?.t2_p1_id) || null
      : startingServerPlayerId || null;
    try {
      await updateDoc(doc(db, 'matches', id), {
        court_positions: JSON.stringify(isSinglesMatch ? {} : courtPos),
        court_number: courtNumber,
        current_server_team: serverTeam === 1 ? match.team1_id : match.team2_id,
        current_server_player_id: serverPlayerId,
        server_number: 2,
        is_first_serve: true,
        status: 'setup',
      });
      setView('score');
    } finally { setSaving(false); }
  };

  const handleStart = async () => {
    if (match?.status !== 'setup') {
      showDialog('Court Setup Required', 'You must configure the court before starting the match. Click "Court Setup" tab.', null);
      return;
    }
    // Verify tournament is approved before allowing match start
    if (match?.tournament_id) {
      try {
        const tournSnap = await getDoc(doc(db, 'tournaments', match.tournament_id));
        if (tournSnap.exists()) {
          const tournData = tournSnap.data();
          if ((tournData.approval_status || 'pending') !== 'approved') {
            showDialog(
              'Approval Required',
              'This tournament must be approved by the super admin before matches can be started.',
              null
            );
            return;
          }
        }
      } catch {
        // If we can't check, allow proceeding (fail open for quick matches without tournament_id)
      }
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'matches', id), {
        status: 'active',
        started_at: new Date().toISOString(),
      });
    } catch (err) {
      showDialog('Error', err.message || 'Could not start match', null);
    } finally { setSaving(false); }
  };

  const handleRally = async (serverWon) => {
    setSaving(true);
    let toastPayload = null;
    let setWinnerPayload = null;
    let matchWinnerId = null;
    let loserTeamId = null;
    let winnerScore = 0, loserScore = 0;
    let matchUpdate_setScores = null;
    const newPointRef = doc(collection(db, 'points'));

    try {
      await runTransaction(db, async (tx) => {
        const matchSnap = await tx.get(doc(db, 'matches', id));
        if (!matchSnap.exists()) throw new Error('Match not found');
        const m = { id: matchSnap.id, ...matchSnap.data() };
        if (m.status !== 'active') throw new Error('Match is not active');

        const isDoubles = m.game_type === 'doubles';
        const ptw = m.points_to_win || 11;
        const currentPos = tryParseJson(m.court_positions);
        const servingTeamId = m.current_server_team;
        const receivingTeamId = servingTeamId === m.team1_id ? m.team2_id : m.team1_id;
        const servingIsTeam1 = servingTeamId === m.team1_id;
        const servingStatic = servingIsTeam1 ? staticRef.current?.team1 : staticRef.current?.team2;
        const receivingStatic = servingIsTeam1 ? staticRef.current?.team2 : staticRef.current?.team1;

        // Use separate singles / doubles serve logic to keep rules isolated.
        let nextPos, nextServerPlayerId, nextServerTeamId, nextServerNum, rallyType;
        if (isDoubles) {
          ({ nextPos, nextServerPlayerId, nextServerTeamId, nextServerNum, rallyType } =
            computeNextServe({
              currentPos,
              currentServerPlayerId: m.current_server_player_id,
              servingTeamId,
              receivingTeamId,
              team1Id: m.team1_id,
              serverNum: m.server_number || 2,
              isFirstServe: m.is_first_serve || false,
              isDoubles: true,
              serverWon,
              servingTeamPlayer2Id: servingStatic?.player2_id || null,
              receivingTeamPlayer1Id: receivingStatic?.player1_id || null,
            }));
        } else {
          ({ nextPos, nextServerPlayerId, nextServerTeamId, nextServerNum, rallyType } =
            computeSinglesNextServe({
              currentServerPlayerId: m.current_server_player_id,
              servingTeamId,
              receivingTeamId,
              serverWon,
              receivingTeamPlayer1Id: receivingStatic?.player1_id || null,
            }));
        }

        let newT1 = m.team1_score;
        let newT2 = m.team2_score;
        if (serverWon) {
          if (servingIsTeam1) newT1++; else newT2++;
        }

        const setWinnerId = checkWinner(newT1, newT2, ptw, m.team1_id, m.team2_id);
        let newT1Sets = m.team1_sets || 0;
        let newT2Sets = m.team2_sets || 0;
        let newStatus = 'active';
        let completedAt = null;
        let effectiveRallyType = setWinnerId ? 'new_set' : rallyType;

        if (setWinnerId) {
          if (setWinnerId === m.team1_id) newT1Sets++; else newT2Sets++;
          const setsNeeded = m.sets_to_win || 1;

          // Append this set's final score to history (before scores reset)
          const existingSetScores = JSON.parse(m.set_scores || '[]');
          existingSetScores.push({ t1: newT1, t2: newT2, winner: setWinnerId });

          if (newT1Sets >= setsNeeded || newT2Sets >= setsNeeded) {
            matchWinnerId = setWinnerId;
            loserTeamId = matchWinnerId === m.team1_id ? m.team2_id : m.team1_id;
            winnerScore = matchWinnerId === m.team1_id ? newT1 : newT2;
            loserScore = matchWinnerId === m.team1_id ? newT2 : newT1;
            newStatus = 'completed';
            completedAt = new Date().toISOString();
          } else {
            // Set done, match continues — prepare banner data
            const n = namesRef.current;
            setWinnerPayload = {
              winnerName: setWinnerId === m.team1_id ? n.team1_name : n.team2_name,
              winnerScore: setWinnerId === m.team1_id ? newT1 : newT2,
              loserScore: setWinnerId === m.team1_id ? newT2 : newT1,
              setNum: existingSetScores.length,
              nextSetNum: existingSetScores.length + 1,
            };
            newT1 = 0; newT2 = 0;
          }

          matchUpdate_setScores = JSON.stringify(existingSetScores);
        }

        const matchUpdate = {
          team1_score: newT1, team2_score: newT2,
          team1_sets: newT1Sets, team2_sets: newT2Sets,
          current_server_team: nextServerTeamId,
          server_number: nextServerNum,
          current_server_player_id: nextServerPlayerId || null,
          court_positions: JSON.stringify(nextPos),
          is_first_serve: false,
          status: newStatus,
          ...(matchUpdate_setScores ? { set_scores: matchUpdate_setScores } : {}),
        };
        if (matchWinnerId) {
          matchUpdate.winner_team_id = matchWinnerId;
          matchUpdate.completed_at = completedAt;
        }

        tx.update(doc(db, 'matches', id), matchUpdate);
        tx.set(newPointRef, {
          match_id: id,
          serving_team_id: servingTeamId,
          serving_player_id: m.current_server_player_id || null,
          server_number_before: m.server_number || 2,
          server_won: serverWon,
          rally_type: effectiveRallyType,
          team1_score_before: m.team1_score,
          team2_score_before: m.team2_score,
          court_positions_before: m.court_positions,
          is_first_serve_before: m.is_first_serve || false,
          team1_score_after: newT1,
          team2_score_after: newT2,
          created_at: new Date().toISOString(),
        });

        // Only show serve-change toasts (not set/match events — those get banners)
        if (effectiveRallyType === 'side_out' || effectiveRallyType === 'second_server') {
          const n = namesRef.current;
          const nextTeamName = nextServerTeamId === m.team1_id ? n.team1_name : n.team2_name;
          const allP = [
            { id: n.t1_p1_id, name: n.t1_p1_name }, { id: n.t1_p2_id, name: n.t1_p2_name },
            { id: n.t2_p1_id, name: n.t2_p1_name }, { id: n.t2_p2_id, name: n.t2_p2_name },
          ].filter(p => p.id);
          const nextPName = allP.find(p => p.id === nextServerPlayerId)?.name || null;
          toastPayload = {
            rally_type: effectiveRallyType,
            message: effectiveRallyType === 'side_out'
              ? `Side Out — ${nextTeamName} now serving${nextPName ? ` · ${nextPName}` : ''}`
              : `2nd Server${nextPName ? ` — ${nextPName}` : ''}`,
          };
        }
      });

      if (toastPayload) {
        setToastNotif(toastPayload);
        setTimeout(() => setToastNotif(null), 3500);
      }
      if (setWinnerPayload) {
        setSetWinnerBanner(setWinnerPayload);
      }
      if (matchWinnerId) {
        triggerWinAnimation({ ...match, winner_team_id: matchWinnerId, status: 'completed' });
        Promise.all([
          updatePlayerStats(matchWinnerId, loserTeamId, winnerScore, loserScore),
          updateDoc(doc(db, 'teams', matchWinnerId), { wins: increment(1), points_for: increment(winnerScore), points_against: increment(loserScore) }),
          updateDoc(doc(db, 'teams', loserTeamId), { losses: increment(1), points_for: increment(loserScore), points_against: increment(winnerScore) }),
        ]).catch(console.error);
      }
    } catch (err) {
      showDialog('Error', err.message || 'An error occurred', null);
    } finally { setSaving(false); }
  };

  const handleUndo = () => {
    showDialog('Undo Last Point', 'Remove the last recorded point?', async () => {
      if (points.length === 0) return;
      const last = points[0];
      try {
        await runTransaction(db, async (tx) => {
          tx.update(doc(db, 'matches', id), {
            team1_score: last.team1_score_before,
            team2_score: last.team2_score_before,
            current_server_team: last.serving_team_id,
            server_number: last.server_number_before,
            current_server_player_id: last.serving_player_id || null,
            court_positions: last.court_positions_before,
            is_first_serve: last.is_first_serve_before || false,
            status: 'active',
            winner_team_id: null,
            completed_at: null,
          });
          tx.delete(doc(db, 'points', last.id));
        });
      } catch (err) {
        showDialog('Error', err.message || 'Could not undo', null);
      }
    });
  };

  const handleForceStop = () => {
    showDialog('Force Stop Match', 'This will immediately end the match without declaring a winner. This cannot be undone.', async () => {
      try {
        await updateDoc(doc(db, 'matches', id), {
          status: 'completed',
          winner_team_id: null,
          completed_at: new Date().toISOString(),
        });
      } catch (err) {
        showDialog('Error', err.message || 'Could not stop match', null);
      }
    });
  };

  const handleRestart = () => {
    showDialog('Restart Match', 'A new match will be created with the same teams and court setup. You can adjust positions before starting.', async () => {
      try {
        const ref = await addDoc(collection(db, 'matches'), {
          tournament_id: match.tournament_id || null,
          team1_id: match.team1_id, team2_id: match.team2_id,
          game_type: match.game_type,
          round: match.round || null, match_order: match.match_order || null,
          points_to_win: match.points_to_win, sets_to_win: match.sets_to_win || 1,
          team1_sets: 0, team2_sets: 0,
          court_number: match.court_number || 1,
          status: 'setup', winner_team_id: null,
          team1_score: 0, team2_score: 0,
          current_server_team: match.team1_id,
          server_number: 2, is_first_serve: true,
          current_server_player_id: null,
          court_positions: match.court_positions || '{}',
          set_scores: '[]',
          scheduled_at: null, started_at: null, completed_at: null,
          created_at: new Date().toISOString(),
        });
        navigate(`/matches/${ref.id}/live`);
      } catch (err) {
        showDialog('Error', err.message || 'Could not restart match', null);
      }
    });
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f1923' }}>
      <div style={{ color: '#fff', fontSize: '18px' }}>Loading match...</div>
    </div>
  );
  if (!match) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f1923' }}>
      <div style={{ color: '#fff' }}>Match not found. <Link to="/dashboard" style={{ color: '#27ae60' }}>Go back</Link></div>
    </div>
  );

  const isAdmin = !!admin;
  const isActive = match.status === 'active';
  const isCompleted = match.status === 'completed';
  const isSetup = match.status === 'setup';
  const isScheduled = match.status === 'scheduled';
  const isDoubles = match.game_type === 'doubles';
  const ptw = match.points_to_win || 11;

  const team1Players = [
    { id: match.t1_p1_id, name: match.t1_p1_name },
    match.t1_p2_id ? { id: match.t1_p2_id, name: match.t1_p2_name } : null,
  ].filter(Boolean);
  const team2Players = [
    { id: match.t2_p1_id, name: match.t2_p1_name },
    match.t2_p2_id ? { id: match.t2_p2_id, name: match.t2_p2_name } : null,
  ].filter(Boolean);
  const allPlayers = [...team1Players, ...team2Players];

  const servingTeamId = match.current_server_team;
  const isT1Serving = servingTeamId === match.team1_id;
  const servingScore = isT1Serving ? match.team1_score : match.team2_score;
  const receivingScore = isT1Serving ? match.team2_score : match.team1_score;
  const serverNum = match.server_number || 2;
  const servingTeamName = isT1Serving ? match.team1_name : match.team2_name;
  const currentServerPlayer = allPlayers.find(p => p.id === match.current_server_player_id) || null;
  const scoreCall = isDoubles
    ? `${servingScore}* – ${receivingScore} – ${serverNum}`
    : `${servingScore}* – ${receivingScore}`;
  // Singles only: which side of the court the server stands on right now.
  const singlesServeSide = !isDoubles ? getSinglesServeSide(servingScore) : null;

  const isMatchPoint1 = match.team1_score >= ptw - 1 && match.team1_score > match.team2_score;
  const isMatchPoint2 = match.team2_score >= ptw - 1 && match.team2_score > match.team1_score;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #0f1923 0%, #1a2332 60%, #0d2b1f 100%)', color: '#fff', paddingBottom: '32px' }}>

      {confetti.map(c => (
        <div key={c.id} style={{
          position: 'fixed', top: '-20px', left: `${c.x}%`, width: c.size, height: c.size,
          background: c.color, borderRadius: '2px', zIndex: 9999,
          animation: `confettiFall ${1.5 + Math.random()}s ${c.delay}s ease-in forwards`,
          transform: 'rotate(45deg)',
        }} />
      ))}

      {animation && <ScoreAnimation animation={animation} match={match} />}

      {dialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#1a2332', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '28px', maxWidth: '340px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
            <div style={{ fontSize: '17px', fontWeight: '800', color: '#fff', marginBottom: '10px' }}>{dialog.title}</div>
            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginBottom: '22px', lineHeight: 1.5 }}>{dialog.message}</div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDialog(null)} style={{ padding: '9px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)' }}>
                {dialog.onConfirm ? 'Cancel' : 'OK'}
              </button>
              {dialog.onConfirm && (
                <button onClick={() => { dialog.onConfirm(); setDialog(null); }} style={{ padding: '9px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', background: '#e74c3c', border: 'none', color: '#fff' }}>
                  Confirm
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {toastNotif && (
        <div style={{
          position: 'fixed', top: '70px', left: '50%', transform: 'translateX(-50%)',
          background: toastNotif.rally_type === 'side_out' ? '#e74c3c' : toastNotif.rally_type === 'second_server' ? '#f39c12' : '#27ae60',
          color: '#fff', padding: '10px 22px', borderRadius: '25px', fontWeight: '700',
          fontSize: '13px', zIndex: 1000, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          whiteSpace: 'nowrap', letterSpacing: '0.5px',
        }}>
          {toastNotif.rally_type === 'side_out' ? '↔ ' : '🔄 '}
          {toastNotif.message}
        </div>
      )}

      {/* Set Winner Banner */}
      {setWinnerBanner && (
        <div onClick={() => setSetWinnerBanner(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1800,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #0d2b1f 0%, #1a2332 100%)',
            border: '2px solid rgba(39,174,96,0.6)', borderRadius: '20px',
            padding: '36px 40px', textAlign: 'center',
            boxShadow: '0 24px 80px rgba(0,0,0,0.8), 0 0 60px rgba(39,174,96,0.15)',
            maxWidth: '320px', width: '100%',
            animation: 'slideUp 0.35s ease',
          }}>
            <div style={{ fontSize: '44px', marginBottom: '10px' }}>🏆</div>
            <div style={{ fontSize: '11px', fontWeight: '800', color: '#f39c12', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '10px' }}>
              Set {setWinnerBanner.setNum} Complete
            </div>
            <div style={{ fontSize: '22px', fontWeight: '900', color: '#2ecc71', marginBottom: '6px' }}>
              {setWinnerBanner.winnerName}
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '12px' }}>wins the set</div>
            <div style={{ fontSize: '40px', fontWeight: '900', letterSpacing: '6px', color: '#fff', marginBottom: '20px' }}>
              {setWinnerBanner.winnerScore}<span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '28px' }}>–</span>{setWinnerBanner.loserScore}
            </div>
            <div style={{ background: 'rgba(243,156,18,0.15)', border: '1px solid rgba(243,156,18,0.3)', borderRadius: '10px', padding: '10px 16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#f39c12' }}>▶ Starting Set {setWinnerBanner.nextSetNum}</div>
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)' }}>Tap anywhere to continue</div>
          </div>
        </div>
      )}

      {/* Tournament poster banner */}
      {match.tournament_poster_url && (
        <div style={{ height: '80px', overflow: 'hidden', position: 'relative' }}>
          <img src={match.tournament_poster_url} alt={match.tournament_name || 'Tournament'}
            style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.55, display: 'block' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 40%, rgba(15,25,35,0.85) 100%)' }} />
          {match.tournament_name && (
            <div style={{ position: 'absolute', bottom: '8px', left: '16px', fontSize: '11px', fontWeight: '800', color: '#fff', letterSpacing: '1px', textTransform: 'uppercase', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
              {match.tournament_name}
            </div>
          )}
        </div>
      )}

      {/* Top Bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {admin && (
            match.tournament_id
              ? <Link to={`/tournaments/${match.tournament_id}`} style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', textDecoration: 'none' }}>← Back</Link>
              : <Link to="/dashboard" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', textDecoration: 'none' }}>← Dashboard</Link>
          )}
          <div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {match.tournament_name || 'Standalone'} · Court {match.court_number}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => { navigator.clipboard.writeText(window.location.href); showDialog('Link Copied!', 'Share this URL with spectators to watch the live score.', null); }}
            style={{ padding: '5px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.15)' }}>
            🔗 Share
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: connected ? '#27ae60' : '#e74c3c' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor', animation: connected ? 'pulse 1.5s infinite' : 'none' }} />
            {connected ? 'LIVE' : 'OFF'}
          </div>
          {(isActive || isCompleted) && match.started_at && (
            <MatchTimer startedAt={match.started_at} endedAt={isCompleted ? match.completed_at : null} compact
              style={{ fontSize: '12px', fontWeight: '700', color: isActive ? '#f39c12' : 'rgba(255,255,255,0.35)',
                background: 'rgba(0,0,0,0.3)', padding: '3px 8px', borderRadius: '6px' }} />
          )}
          <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
            background: isActive ? 'rgba(39,174,96,0.3)' : isCompleted ? 'rgba(41,128,185,0.3)' : 'rgba(255,255,255,0.1)',
            color: isActive ? '#2ecc71' : isCompleted ? '#3498db' : 'rgba(255,255,255,0.6)' }}>
            {isActive ? '● LIVE' : isCompleted ? '✓ FINAL' : isSetup ? '⚙ SETUP' : '⏳ SCHED'}
          </span>
        </div>
      </div>

      {/* Score Display */}
      <div style={{ padding: '16px 12px 8px', background: 'rgba(0,0,0,0.15)' }}>
        {match.sets_to_win > 1 && (() => {
          const prevSets = JSON.parse(match.set_scores || '[]');
          const currentSet = prevSets.length + 1;
          const totalSets = (match.sets_to_win || 1) * 2 - 1;
          return (
            <div style={{ marginBottom: '12px' }}>
              {/* Current set badge */}
              <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '11px', fontWeight: '800', color: '#f39c12', background: 'rgba(243,156,18,0.15)', border: '1px solid rgba(243,156,18,0.3)', padding: '3px 14px', borderRadius: '20px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                  {isCompleted ? `Match Over · Best of ${totalSets}` : `Set ${currentSet} of ${totalSets}`}
                </span>
              </div>
              {/* Previous sets */}
              {prevSets.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Prev:</span>
                  {prevSets.map((s, i) => {
                    const t1Won = s.winner === match.team1_id;
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)' }}>S{i + 1}</span>
                        <span style={{ fontSize: '13px', fontWeight: '800', color: t1Won ? '#2ecc71' : 'rgba(255,255,255,0.7)', background: t1Won ? 'rgba(39,174,96,0.15)' : 'rgba(255,255,255,0.05)', borderRadius: '5px', padding: '2px 7px', letterSpacing: '1px' }}>
                          {s.t1}
                        </span>
                        <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.2)' }}>–</span>
                        <span style={{ fontSize: '13px', fontWeight: '800', color: !t1Won ? '#2ecc71' : 'rgba(255,255,255,0.7)', background: !t1Won ? 'rgba(39,174,96,0.15)' : 'rgba(255,255,255,0.05)', borderRadius: '5px', padding: '2px 7px', letterSpacing: '1px' }}>
                          {s.t2}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Sets tally */}
              <div style={{ textAlign: 'center', fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>
                Sets: <span style={{ color: '#2ecc71', fontWeight: '700' }}>{match.team1_sets || 0}</span>
                <span style={{ margin: '0 4px' }}>–</span>
                <span style={{ color: '#2ecc71', fontWeight: '700' }}>{match.team2_sets || 0}</span>
                {' '}({match.team1_name} – {match.team2_name})
              </div>
            </div>
          );
        })()}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {/* Team 1 */}
          <div style={{ flex: 1, textAlign: 'center', position: 'relative', padding: '12px 8px', borderRadius: '12px', background: isMatchPoint1 ? 'rgba(39,174,96,0.12)' : 'rgba(255,255,255,0.04)', border: `1px solid ${isMatchPoint1 ? 'rgba(39,174,96,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#fff', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{match.team1_name}</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team1Players.map(p => p.name).join(' & ')}</div>
            <div style={{ fontSize: '64px', fontWeight: '900', lineHeight: 1, color: match.winner_team_id === match.team1_id ? '#2ecc71' : '#fff' }}>
              {match.team1_score}
            </div>
            {isT1Serving && isActive && (
              <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#27ae60', letterSpacing: '0.5px' }}>
                  ● SERVING{isDoubles ? ` · S${serverNum}` : singlesServeSide ? ` · ${singlesServeSide === 'right' ? 'Right' : 'Left'}` : ''}
                </div>
                {currentServerPlayer && (
                  <div style={{ fontSize: '13px', fontWeight: '800', color: '#FFD700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                    {currentServerPlayer.name}
                  </div>
                )}
              </div>
            )}
            {isMatchPoint1 && isActive && <div style={{ fontSize: '9px', color: '#2ecc71', fontWeight: '700', marginTop: '4px', letterSpacing: '1px', animation: 'pulse 1s infinite' }}>MATCH POINT</div>}
            {match.winner_team_id === match.team1_id && <div style={{ fontSize: '18px', marginTop: '2px' }}>🏆</div>}
          </div>

          {/* Center */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: '44px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'rgba(255,255,255,0.2)' }}>VS</div>
            <div style={{ width: '2px', height: '20px', background: 'rgba(255,255,255,0.07)', borderRadius: '2px' }} />
            {(isActive || isCompleted) && match.started_at && (
              <MatchTimer startedAt={match.started_at} endedAt={isCompleted ? match.completed_at : null} compact
                style={{ fontSize: '13px', fontWeight: '800', color: isActive ? '#f39c12' : 'rgba(255,255,255,0.3)', letterSpacing: '0.5px' }} />
            )}
            <div style={{ width: '2px', height: '20px', background: 'rgba(255,255,255,0.07)', borderRadius: '2px' }} />
          </div>

          {/* Team 2 */}
          <div style={{ flex: 1, textAlign: 'center', position: 'relative', padding: '12px 8px', borderRadius: '12px', background: isMatchPoint2 ? 'rgba(39,174,96,0.12)' : 'rgba(255,255,255,0.04)', border: `1px solid ${isMatchPoint2 ? 'rgba(39,174,96,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#fff', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{match.team2_name}</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team2Players.map(p => p.name).join(' & ')}</div>
            <div style={{ fontSize: '64px', fontWeight: '900', lineHeight: 1, color: match.winner_team_id === match.team2_id ? '#2ecc71' : '#fff' }}>
              {match.team2_score}
            </div>
            {!isT1Serving && isActive && (
              <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#27ae60', letterSpacing: '0.5px' }}>
                  ● SERVING{isDoubles ? ` · S${serverNum}` : singlesServeSide ? ` · ${singlesServeSide === 'right' ? 'Right' : 'Left'}` : ''}
                </div>
                {currentServerPlayer && (
                  <div style={{ fontSize: '13px', fontWeight: '800', color: '#FFD700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                    {currentServerPlayer.name}
                  </div>
                )}
              </div>
            )}
            {isMatchPoint2 && isActive && <div style={{ fontSize: '9px', color: '#2ecc71', fontWeight: '700', marginTop: '4px', letterSpacing: '1px', animation: 'pulse 1s infinite' }}>MATCH POINT</div>}
            {match.winner_team_id === match.team2_id && <div style={{ fontSize: '18px', marginTop: '2px' }}>🏆</div>}
          </div>
        </div>
      </div>

      {/* Winner Banner */}
      {isCompleted && (
        <div style={{ textAlign: 'center', padding: '14px 12px', background: 'linear-gradient(90deg, rgba(39,174,96,0.2), rgba(39,174,96,0.4), rgba(39,174,96,0.2))', animation: match.winner_team_id ? 'glow 2s infinite' : 'none' }}>
          {match.winner_team_id ? (
            <>
              <div style={{ fontSize: '18px', fontWeight: '800', letterSpacing: '2px' }}>
                🏆 {match.winner_team_id === match.team1_id ? match.team1_name : match.team2_name} WINS!
              </div>
              {match.sets_to_win > 1 && (() => {
                const prevSets = JSON.parse(match.set_scores || '[]');
                return (
                  <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: '700' }}>
                      {match.team1_sets || 0}–{match.team2_sets || 0} sets
                    </span>
                    {prevSets.length > 0 && (
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                        ({prevSets.map((s, i) => `${s.t1}–${s.t2}`).join(', ')})
                      </span>
                    )}
                  </div>
                );
              })()}
            </>
          ) : (
            <span style={{ fontSize: '15px', color: 'rgba(255,255,255,0.6)' }}>⏹ Match Stopped</span>
          )}
        </div>
      )}

      {/* Restart panel */}
      {isAdmin && isCompleted && (
        <div style={{ margin: '0 12px', marginTop: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#fff', marginBottom: '3px' }}>Rematch?</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Creates a new match with the same teams and court setup.</div>
          </div>
          <button onClick={handleRestart}
            style={{ padding: '10px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', background: 'rgba(39,174,96,0.25)', border: '1px solid rgba(39,174,96,0.5)', color: '#2ecc71', whiteSpace: 'nowrap' }}>
            🔁 Restart
          </button>
        </div>
      )}

      {/* Admin Tab Bar */}
      {isAdmin && (
        <div style={{ display: 'flex', gap: '0', padding: '0 12px', background: 'rgba(0,0,0,0.2)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {[
            { key: 'score', label: '🏓 Score' },
            { key: 'setup', label: '⚙️ Court Setup' },
            { key: 'points', label: '📋 Log' },
          ].map(t => (
            <button key={t.key} onClick={() => setView(t.key)}
              style={{ flex: 1, padding: '10px 4px', fontSize: '12px', fontWeight: '600', background: 'transparent', border: 'none', cursor: 'pointer', transition: 'all 0.2s', borderBottom: view === t.key ? '2px solid #27ae60' : '2px solid transparent', color: view === t.key ? '#2ecc71' : 'rgba(255,255,255,0.4)' }}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ padding: '12px' }}>

        {/* Score Tab */}
        {(!isAdmin || view === 'score') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

            {isAdmin && isScheduled && (
              <div style={{ background: 'rgba(243,156,18,0.15)', border: '1px solid rgba(243,156,18,0.4)', borderRadius: '10px', padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#f39c12', marginBottom: '6px' }}>⚙️ Match Setup Required</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '12px' }}>
                  {isDoubles
                    ? 'Configure court positions and starting server before the match can begin.'
                    : 'Select the starting server before the match can begin.'}
                </div>
                <button className="btn btn-primary" onClick={() => setView('setup')}>Go to Match Setup →</button>
              </div>
            )}

            {isAdmin && isActive && (
              <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '14px' }}>
                <div style={{ textAlign: 'center', marginBottom: '14px', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Score Call</div>
                  <div style={{ fontSize: '28px', fontWeight: '900', letterSpacing: '4px', color: '#fff', fontFamily: 'monospace' }}>{scoreCall}</div>
                  <div style={{ fontSize: '12px', color: '#27ae60', marginTop: '6px', fontWeight: '700' }}>
                    {servingTeamName} serving
                    {isDoubles && ` · Server ${serverNum}`}
                    {!isDoubles && singlesServeSide && (
                      <span style={{ color: '#3498db' }}>
                        {' · '}{singlesServeSide === 'right' ? 'Right side' : 'Left side'}
                      </span>
                    )}
                    {currentServerPlayer && <span style={{ color: '#FFD700' }}> · {currentServerPlayer.name}</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px' }}>
                  <button onClick={() => handleRally(true)} disabled={saving}
                    style={{ width: '100%', padding: '18px', borderRadius: '10px', fontSize: '16px', fontWeight: '800',
                      background: 'rgba(39,174,96,0.25)', color: '#2ecc71', border: '2px solid rgba(39,174,96,0.5)',
                      cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                    🏓 Server Won
                  </button>
                  <button onClick={() => handleRally(false)} disabled={saving}
                    style={{ width: '100%', padding: '18px', borderRadius: '10px', fontSize: '16px', fontWeight: '800',
                      background: 'rgba(231,76,60,0.2)', color: '#e74c3c', border: '2px solid rgba(231,76,60,0.4)',
                      cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                    ✕ Server Lost
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
                  <button onClick={handleUndo}
                    style={{ flex: 1, padding: '8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'rgba(255,255,255,0.35)' }}>
                    ↩ Undo Last Point
                  </button>
                  <button onClick={handleForceStop}
                    style={{ padding: '8px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid rgba(231,76,60,0.35)', background: 'rgba(231,76,60,0.1)', color: 'rgba(231,76,60,0.7)' }}>
                    ⏹ Stop
                  </button>
                </div>
              </div>
            )}

            {isAdmin && isSetup && (
              <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(39,174,96,0.3)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginBottom: '12px' }}>Court configured. Ready to start!</div>
                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: '16px', padding: '14px' }}
                  onClick={handleStart} disabled={saving}>
                  {saving ? '...' : '▶ Start Match'}
                </button>
                <button onClick={handleForceStop}
                  style={{ marginTop: '8px', width: '100%', padding: '8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid rgba(231,76,60,0.3)', background: 'transparent', color: 'rgba(231,76,60,0.6)' }}>
                  ⏹ Abandon Match
                </button>
              </div>
            )}

            {isDoubles ? (
              <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '12px' }}>
                <CourtLayout
                  match={match}
                  team1Players={team1Players}
                  team2Players={team2Players}
                  courtPositions={tryParseJson(match.court_positions)}
                  isAdmin={false}
                  onPositionChange={(pos) => setCourtPos(pos)}
                  serverTeamId={match.current_server_team}
                  serverPlayerId={match.current_server_player_id}
                />
              </div>
            ) : isActive && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '14px' }}>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Singles — Serve Position</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {/* Right side */}
                  <div style={{
                    flex: 1, padding: '14px 10px', borderRadius: '10px', textAlign: 'center',
                    background: singlesServeSide === 'right' ? 'rgba(39,174,96,0.2)' : 'rgba(255,255,255,0.04)',
                    border: `2px solid ${singlesServeSide === 'right' ? 'rgba(39,174,96,0.6)' : 'rgba(255,255,255,0.08)'}`,
                  }}>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>→</div>
                    <div style={{ fontSize: '12px', fontWeight: '800', color: singlesServeSide === 'right' ? '#2ecc71' : 'rgba(255,255,255,0.3)' }}>RIGHT SIDE</div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>Even score</div>
                    {singlesServeSide === 'right' && <div style={{ fontSize: '10px', color: '#2ecc71', fontWeight: '700', marginTop: '6px', letterSpacing: '1px' }}>● NOW SERVING</div>}
                  </div>
                  {/* Left side */}
                  <div style={{
                    flex: 1, padding: '14px 10px', borderRadius: '10px', textAlign: 'center',
                    background: singlesServeSide === 'left' ? 'rgba(52,152,219,0.2)' : 'rgba(255,255,255,0.04)',
                    border: `2px solid ${singlesServeSide === 'left' ? 'rgba(52,152,219,0.6)' : 'rgba(255,255,255,0.08)'}`,
                  }}>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>←</div>
                    <div style={{ fontSize: '12px', fontWeight: '800', color: singlesServeSide === 'left' ? '#3498db' : 'rgba(255,255,255,0.3)' }}>LEFT SIDE</div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>Odd score</div>
                    {singlesServeSide === 'left' && <div style={{ fontSize: '10px', color: '#3498db', fontWeight: '700', marginTop: '6px', letterSpacing: '1px' }}>● NOW SERVING</div>}
                  </div>
                </div>
              </div>
            )}

            {isActive && points.length > 0 && (
              <div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Recent Points</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {points.slice(0, 5).map((pt, i) => {
                    const isT1Server = pt.serving_team_id === match.team1_id;
                    const icon = pt.rally_type === 'side_out' ? '↔' : pt.server_won ? '🏓' : '🔄';
                    return (
                      <div key={pt.id || i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', borderRadius: '6px', background: pt.server_won ? 'rgba(39,174,96,0.08)' : 'rgba(231,76,60,0.07)', border: `1px solid ${pt.server_won ? 'rgba(39,174,96,0.15)' : 'rgba(231,76,60,0.15)'}` }}>
                        <span style={{ fontSize: '13px' }}>{icon}</span>
                        <div style={{ flex: 1, fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                          {pt.server_won ? 'Won' : pt.rally_type === 'side_out' ? 'Side Out' : '2nd Server'} · {isT1Server ? match.team1_name : match.team2_name}
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: 'rgba(255,255,255,0.6)' }}>
                          {pt.team1_score_after} – {pt.team2_score_after}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Court Setup Tab */}
        {isAdmin && view === 'setup' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Court diagram — doubles only */}
            {isDoubles && (
              <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '12px' }}>
                <CourtLayout
                  match={match}
                  team1Players={team1Players}
                  team2Players={team2Players}
                  courtPositions={courtPos}
                  isAdmin={true}
                  onPositionChange={setCourtPos}
                  serverTeamId={serverTeam === 1 ? match.team1_id : match.team2_id}
                  serverPlayerId={startingServerPlayerId}
                />
              </div>
            )}

            {/* Singles scoring rules reminder */}
            {!isDoubles && (
              <div style={{ background: 'rgba(52,152,219,0.1)', border: '1px solid rgba(52,152,219,0.3)', borderRadius: '10px', padding: '12px 14px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#3498db', marginBottom: '6px' }}>Singles Scoring Rules</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
                  Only the server scores · Score = server – receiver · Even score → Right side · Odd score → Left side · First to 11, win by 2
                </div>
              </div>
            )}

            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '14px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#fff', marginBottom: '14px' }}>
                {isDoubles ? '⚙️ Court Configuration' : '⚙️ Match Setup'}
              </h3>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>Court Number</label>
                <input type="number" min="1" max="20" value={courtNumber}
                  onChange={e => setCourtNumber(+e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: '14px' }} />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>Starting Server</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {[{ val: 1, label: match.team1_name }, { val: 2, label: match.team2_name }].map(opt => (
                    <button key={opt.val} type="button" onClick={() => { setServerTeam(opt.val); setStartingServerPlayerId(null); }}
                      style={{ padding: '10px', borderRadius: '8px', fontWeight: '600', fontSize: '12px', cursor: 'pointer', border: '2px solid', borderColor: serverTeam === opt.val ? '#27ae60' : 'transparent', background: serverTeam === opt.val ? 'rgba(39,174,96,0.2)' : 'rgba(255,255,255,0.08)', color: serverTeam === opt.val ? '#fff' : 'rgba(255,255,255,0.5)' }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Doubles only: who serves first (0-0-2 rule) */}
              {isDoubles && (
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' }}>
                    Who Serves First? <span style={{ color: '#f39c12', fontSize: '10px', textTransform: 'none' }}>(0-0-2 rule · one chance only)</span>
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '6px' }}>
                    {(serverTeam === 1 ? team1Players : team2Players).map((p, idx) => (
                      <button key={p.id} type="button" onClick={() => setStartingServerPlayerId(p.id)}
                        style={{ padding: '10px', borderRadius: '8px', fontWeight: '600', fontSize: '12px', cursor: 'pointer', border: '2px solid', borderColor: startingServerPlayerId === p.id ? '#f39c12' : 'transparent', background: startingServerPlayerId === p.id ? 'rgba(243,156,18,0.2)' : 'rgba(255,255,255,0.08)', color: startingServerPlayerId === p.id ? '#fff' : 'rgba(255,255,255,0.5)' }}>
                        S{idx + 1}: {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Doubles only: court position editor */}
              {isDoubles && (
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>
                    Player Positions <span style={{ color: '#e74c3c' }}>* (required)</span>
                  </label>
                  <CourtPositionEditor team1Players={team1Players} team2Players={team2Players} positions={courtPos} onChange={setCourtPos} />
                </div>
              )}

              <button onClick={handleSetup} disabled={saving}
                style={{ width: '100%', padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: '700', background: '#27ae60', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving...' : isDoubles ? '✓ Save Court Setup' : '✓ Save & Continue'}
              </button>
            </div>
          </div>
        )}

        {/* Point Log Tab */}
        {isAdmin && view === 'points' && (
          <div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#fff', marginBottom: '12px' }}>Point History</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {points.length === 0 ? (
                <div style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '32px' }}>No points recorded yet</div>
              ) : points.map((pt, i) => {
                const isT1Server = pt.serving_team_id === match.team1_id;
                const icon = pt.rally_type === 'side_out' ? '↔' : pt.rally_type === 'second_server' ? '🔄' : pt.server_won ? '🏓' : '↔';
                const servingPlayerName = allPlayers.find(p => p.id === pt.serving_player_id)?.name || null;
                return (
                  <div key={pt.id || i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', background: pt.server_won ? 'rgba(39,174,96,0.08)' : 'rgba(231,76,60,0.07)', border: `1px solid ${pt.server_won ? 'rgba(39,174,96,0.2)' : 'rgba(231,76,60,0.15)'}`, animation: i === 0 ? 'slideUp 0.3s ease' : 'none' }}>
                    <span style={{ fontSize: '15px' }}>{icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#fff' }}>
                        {pt.server_won ? 'Server Won' : pt.rally_type === 'second_server' ? '2nd Server' : 'Side Out'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                        {isT1Server ? match.team1_name : match.team2_name}
                        {pt.server_number_before ? ` · S${pt.server_number_before}` : ''}
                        {servingPlayerName ? ` · ${servingPlayerName}` : ''}
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '800', color: 'rgba(255,255,255,0.7)' }}>
                      {pt.team1_score_after} – {pt.team2_score_after}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CourtPositionEditor({ team1Players, team2Players, positions, onChange }) {
  const rows = [
    { zone: 'TL', label: 'T1 Back-court', team: 1 },
    { zone: 'BL', label: 'T1 Kitchen',    team: 1 },
    { zone: 'TR', label: 'T2 Back-court', team: 2 },
    { zone: 'BR', label: 'T2 Kitchen',    team: 2 },
  ];
  const assignPlayer = (zone, playerId) => {
    const newPos = Object.fromEntries(Object.entries(positions).filter(([, pid]) => pid !== playerId));
    if (playerId) newPos[zone] = playerId;
    else delete newPos[zone];
    onChange(newPos);
  };
  const selStyle = { width: '100%', padding: '8px 10px', borderRadius: '6px', background: '#0f1923', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontSize: '13px', cursor: 'pointer' };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
      {rows.map(({ zone, label, team }) => {
        const assignedId = positions[zone];
        const color = team === 1 ? '#27ae60' : '#2980b9';
        return (
          <div key={zone}>
            <div style={{ fontSize: '10px', fontWeight: '600', color, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
            <select style={selStyle} value={assignedId || ''} onChange={e => assignPlayer(zone, e.target.value || null)}>
              <option value="" style={{ background: '#1a2332' }}>— Empty —</option>
              {(team === 1 ? team1Players : team2Players).map(p => <option key={p.id} value={p.id} style={{ background: '#1a2332' }}>{p.name}</option>)}
            </select>
          </div>
        );
      })}
    </div>
  );
}
